'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const testimonials = [
{
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1c0007e95-1763295050768.png",
  alt: 'Femme souriante aux cheveux châtains, directrice de conciergerie',
  name: 'Camille Renard',
  role: 'Directrice, Conciergerie Azur',
  city: 'Nice · 18 logements',
  quote: 'Avant GiteZen, je passais 3h par jour sur des tâches manuelles. Aujourd\'hui, c\'est automatisé. J\'ai pu prendre 4 nouveaux biens sans recruter.',
  highlight: '–3h/jour',
  status: 'online'
},
{
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_10addbab5-1763301270151.png",
  alt: 'Homme en costume souriant, gestionnaire immobilier professionnel',
  name: 'Thomas Meunier',
  role: 'Gestionnaire, Gîtes du Luberon',
  city: 'Avignon · 31 logements',
  quote: 'La synchronisation des calendriers a éliminé toutes nos surréservations. En 6 semaines, les revenus ont augmenté de 22% grâce à la tarification dynamique.',
  highlight: '+22% revenus',
  status: 'offline'
},
{
  photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1493794a7-1767889999112.png",
  alt: 'Femme professionnelle avec tablette, gérante de location saisonnière',
  name: 'Sophie Durand',
  role: 'Fondatrice, Conciergerie Bretagne',
  city: 'Rennes · 12 logements',
  quote: 'L\'onboarding personnalisé a tout changé. En une heure, tout était configuré. Le badge partenaire a renforcé notre crédibilité auprès des propriétaires.',
  highlight: '1h setup',
  status: 'online'
}];


export default function TestimonialsSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-el').forEach((el, i) => {
              setTimeout(() => el.classList.add('revealed'), i * 120);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-28 px-6 border-t border-white/[0.05]" ref={sectionRef}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 reveal-el">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.02] mb-6">
            <Icon name="StarIcon" size={12} className="text-gold" variant="solid" />
            <span className="text-[10px] tracking-[0.2em] uppercase text-cream/60 font-semibold">Validation terrain</span>
          </div>
          <h2 className="font-fraunces text-5xl md:text-6xl font-light italic leading-tight text-cream">
            Ce que disent nos<br />
            <span className="text-moss-light">partenaires bêta.</span>
          </h2>
        </div>

        {/* Testimonials grid */}
        <div className="grid md:grid-cols-3 gap-5">
          {testimonials?.map((t, i) =>
          <div
            key={i}
            className="spotlight-card glass-card rounded-2xl p-7 flex flex-col justify-between gap-6 reveal-el group">
            
              {/* Profile */}
              <div className="flex items-start justify-between border-b border-white/[0.06] pb-5">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <AppImage
                    src={t?.photo}
                    alt={t?.alt}
                    width={40}
                    height={40}
                    className="w-10 h-10 rounded-lg object-cover grayscale opacity-70 group-hover:grayscale-0 group-hover:opacity-100 transition-all duration-500" />
                  
                    <div
                    className={`absolute -bottom-1 -right-1 w-2.5 h-2.5 rounded-full border-2 border-forest ${
                    t?.status === 'online' ? 'bg-emerald-400' : 'bg-zinc-500'}`
                    } />
                  
                  </div>
                  <div>
                    <p className="text-cream text-sm font-semibold">{t?.name}</p>
                    <p className="text-cream/40 text-[10px] uppercase tracking-wider mt-0.5">{t?.role}</p>
                  </div>
                </div>
                <span className="text-[10px] font-bold text-moss-light bg-moss/10 px-2 py-1 rounded-full whitespace-nowrap">
                  {t?.highlight}
                </span>
              </div>

              {/* Quote */}
              <p className="font-fraunces text-lg font-light italic leading-snug text-cream/80 flex-1">
                "{t?.quote}"
              </p>

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-white/[0.04]">
                <span className="text-[10px] text-cream/30 font-medium">{t?.city}</span>
                <Icon name="ArrowUpRightIcon" size={14} className="text-moss/40" />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

}